import { AdminService } from '@admin/services/admin.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IOption } from '@shared/models/icommons.model';
import { ILocationPermission, IUser, IUserPermission } from '@shared/models/iuser.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { take } from 'rxjs/operators';
import { Data } from '@shared/models/data-object.model';

@Component({
  selector: 'miq-add-location',
  templateUrl: './add-location.component.html',
  styleUrls: ['./add-location.component.scss'],
  standalone: false,
})
export class AddLocationComponent implements OnInit, OnDestroy {
  public selectedMarketsCount: number;
  public selectedDistrictCount: number;
  public selectedNeighborCount: number;
  public userPermissions: IUserPermission;
  public invalidUserPermissions = true;
  public districtsList: IOption[] = [];
  public neighborhoodList: IOption[] = [];
  /*
   * NOTE: hard-coding access status. Previously, it was using the values from the lookup API.
   * However, the status object was being used based on item position in the response array,
   * which caused issues whenever the values order changed.
   */
  private approvedStatus = Object.freeze({ key: 1, value: 'Approved' });
  private pendingStatus = Object.freeze({ key: 3, value: 'Pending' });
  private ADMIN = 'Admin';

  public globalRegion: IOption;
  public confidentialityLevels: IOption[] = [];
  public permissionRecord: ILocationPermission; // record which is to be editted
  public recordsArray: ILocationPermission[] = [];
  public subMarket: number;
  public district: number;
  public neighborhood: number;
  public pageType: string;
  public userInfo: IUser;
  public confidentiality: IOption;
  public formGroup: UntypedFormGroup;
  public showItems = false;
  private id: number;

  constructor(
    public dialogRef: MatDialogRef<AddLocationComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public injectedData: Data,
    private httpService: AdminService,
    private formBuilder: UntypedFormBuilder,
    private resolver: UserInfoResolver
  ) {
    this.formGroup = this.formBuilder.group({
      businessJustification: ['', [Validators.required, Validators.maxLength(250), this.whitespaceValidator]],
      confidentialityFC: ['', [Validators.required]],
    });
    this.userPermissions = {
      globalRegion: { key: -1, value: '' },
      country: { key: -1, value: '' },
      division: { key: -1, value: '' },
      market: { key: -1, value: '' },
      subMarket: [{ key: -1, value: '' }],
      district: [{ key: -1, value: '' }],
      productType: { key: -1, value: '' },
      neighborhood: [{ key: -1, value: '' }],
    };
  }

  ngOnInit() {
    this.httpService.getUserConfidentialityLevels().pipe(take(1)).subscribe(this.initUserSettings.bind(this));
  }

  private initUserSettings(confLevels: IOption[]) {
    this.confidentialityLevels = confLevels;

    this.userInfo = this.resolver.resolve();
    this.permissionRecord = this.injectedData.locationPermission;
    this.pageType = this.injectedData.pageType;
    if (this.permissionRecord) {
      const { confidentialityTypeId: key } = this.permissionRecord;
      this.confidentiality =
        this.confidentialityLevels.find(option => option.key === key) || this.confidentialityLevels[0];
      this.setConfidentialityLevel(this.confidentiality);

      if (this.permissionRecord && this.permissionRecord.businessJustification) {
        this.businessJustification.setValue(this.permissionRecord.businessJustification);
      }
    }
    this.id = this.injectedData.locationPermission ? this.injectedData.locationPermission.id : -1;
  }

  // Advanced search event handler
  public openSubselection(event) {
    if (event === 'close') {
      this.showItems = false;
      return;
    }
    if (event) {
      this.showItems = !this.showItems;
      if (this.permissionRecord && this.permissionRecord.subMarketId) {
        this.subMarket = this.permissionRecord.subMarketId;
        this.userPermissions.subMarket = [
          { key: this.permissionRecord.subMarketId, value: this.permissionRecord.subMarketVal },
        ];
        this.getDistricts(this.subMarket, true);
        this.selectedMarketsCount = 1;
      }
      if (this.permissionRecord && this.permissionRecord.districtId) {
        this.district = this.permissionRecord.districtId;
        this.userPermissions.district = [
          { key: this.permissionRecord.districtId, value: this.permissionRecord.districtVal },
        ];
        this.getDistricts(this.subMarket, true);
        this.selectedDistrictCount = 1;
      } else {
        this.getDistricts(event.key, true);
      }

      if (this.permissionRecord && this.permissionRecord.neighborhoodId) {
        this.neighborhood = this.permissionRecord.neighborhoodId;
        this.userPermissions.neighborhood = [
          { key: this.permissionRecord.neighborhoodId, value: this.permissionRecord.neighborhoodVal },
        ];
        this.getNeighbourhoods(this.district);
        this.selectedDistrictCount = 1;
      }
    }
  }

  public getDistricts(selectedSubmarket, key?: boolean) {
    if (selectedSubmarket) {
      const selectedValue = key ? selectedSubmarket : selectedSubmarket[0].key;
      this.httpService
        .getDistricts(selectedValue)
        .pipe(take(1))
        .subscribe(result => {
          this.districtsList = result;
        });
      if (this.permissionRecord && this.permissionRecord.districtId) {
        this.district = this.permissionRecord.districtId;
      }
    }
  }

  public getNeighbourhoods(district) {
    if (district) {
      this.httpService
        .getNeighbourhoods(district)
        .pipe(take(1))
        .subscribe(result => {
          this.neighborhoodList = result;
        });
    }
  }

  // submarket change handler
  public selectedSubMarkets(submarkets) {
    if (submarkets) {
      if (submarkets.length > 0) {
        this.getDistricts(submarkets);
        this.userPermissions.subMarket = submarkets;
      } else {
        this.districtsList = [];
      }
      this.selectedMarketsCount = submarkets.length;
    }
  }

  // district change handler
  public selectedDistricts(district) {
    if (district && district.length > 0) {
      return this.httpService
        .getNeighbourhoods(district[0].key)
        .pipe(take(1))
        .subscribe(this.onSelectedDistricts.bind(this, district));
    }
  }

  onSelectedDistricts(district, result) {
    this.neighborhoodList = result;
    this.selectedDistrictCount = district.length;
    this.userPermissions.district = district;
  }
  // neighbourhood change handler
  public selectedNeighbourhood(event) {
    if (event) {
      this.userPermissions.neighborhood = event;
    }
  }

  // get user permissions handler
  public setUserPermissions(event) {
    if (event) {
      this.userPermissions = event;
      this.invalidUserPermissions = Object.values(this.userPermissions).some(v => v == null);
    }
  }

  // final model submit
  public addPermission() {
    if (this.pageType === this.ADMIN) {
      this.businessJustification.clearValidators();
      this.businessJustification.updateValueAndValidity();
    }
    if (this.formGroup.valid) {
      // this one assigns the status. if the page is user, it means that an admin user is adding the locations
      this.userPermissions.status =
        // There are three places where a new location permission is added:
        //    Access Request, Admin Users, and User Profile.
        // pageType is "user" in both the Access Request and the User Profile pages
        // pageType is "admin" in the Admin module.
        this.pageType === this.ADMIN ? this.approvedStatus : this.pendingStatus;
      this.getGeographicLevels(this.userPermissions);
      this.dialogRef.close(this.recordsArray);
    } else {
      // display validation errors when clicking 'Add' so users can fix them
      Object.values(this.formGroup.controls).forEach(control => control.markAsTouched({ onlySelf: true }));
    }
  }

  public onCancel() {
    this.dialogRef.close();
  }

  public formPermissionRecord(userPermission: IUserPermission, heirarchyLevel: number) {
    const geoLevels = ['district', 'neighborhood'];
    if (userPermission[geoLevels[heirarchyLevel]] && userPermission[geoLevels[heirarchyLevel]].length > 0) {
      userPermission[geoLevels[heirarchyLevel]].forEach(object => {
        const record = new ILocationPermission();
        record[geoLevels[heirarchyLevel] + 'Id'] = object.key;
        record[geoLevels[heirarchyLevel] + 'Val'] = object.value;
        for (let index = 0; index < heirarchyLevel; index++) {
          record[geoLevels[index] + 'Id'] = userPermission[geoLevels[index]][0].key;
          record[geoLevels[index] + 'Val'] = userPermission[geoLevels[index]][0].value;
        }
        this.setUserPermissionDefaultValuesAndAddToList(record, userPermission);
      });
    }
  }

  public getGeographicLevels(userPermissions: IUserPermission) {
    let geoLevelIndex;
    if (this.userPermissions) {
      if (this.userPermissions.neighborhood && this.userPermissions.neighborhood.length > 0) {
        geoLevelIndex = 1;
        this.formPermissionRecord(userPermissions, geoLevelIndex);
      } else if (this.userPermissions.district && this.userPermissions.district.length > 0) {
        geoLevelIndex = 0;
        this.formPermissionRecord(userPermissions, geoLevelIndex);
      } else {
        const record = new ILocationPermission();
        this.setUserPermissionDefaultValuesAndAddToList(record, this.userPermissions);
      }
    }
  }

  public setUserPermissionDefaultValuesAndAddToList(record: ILocationPermission, data) {
    if (record && data) {
      record.divisionId = data.division.key;
      record.divisionVal = data.division.value;
      record.divisionName = data.division.name;
      record.globalRegionId = data.globalRegion.key;
      record.globalRegionVal = data.globalRegion.value;
      record.globalRegionName = data.globalRegion.name;
      record.marketId = data.market.key;
      record.marketVal = data.market.value;
      record.marketName = data.market.name;
      record.neighborhoodId = data.neighborhood && data.neighborhood[0] ? data.neighborhood[0].key : 0;
      record.districtId = data.district && data.district[0] ? data.district[0].key : 0;
      record.subMarketId = data.submarket.key;
      record.subMarketVal = data.submarket.value;
      record.subMarketName = data.submarket.name;
      record.productTypeId = data.productType.key;
      record.productTypeVal = data.productType.value;
      record.productTypeName = data.productType.value;
      record.propertyUsageTypeId = data.productType.key;
      record.propertyUsageTypeVal = data.productType.value;
      record.propertyUsageTypeName = data.productType.value;
      record.accessRequestStatusId = data.status.key;
      record.accessRequestStatusVal = data.status.value;
      // confidentiality
      record.confidentialityTypeId = this.confidentialityFC.value.key;
      record.confidentialityTypeVal = this.confidentialityFC.value.value;
      // business justification
      record.businessJustification = this.businessJustification.value;

      record.updateBy = this.userInfo.emailAddress;
      record.createBy = this.userInfo.emailAddress;
      record.id = this.id;
      this.recordsArray.push(record);
    }
  }

  public whitespaceValidator(control: UntypedFormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    return !isWhitespace ? null : { whitespace: true };
  }

  public get businessJustification() {
    return this.formGroup.get('businessJustification');
  }
  public get confidentialityFC() {
    return this.formGroup.get('confidentialityFC');
  }

  private setConfidentialityLevel(confidentiality: IOption) {
    this.confidentialityFC.setValue(confidentiality);
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }
}
